import React, { useState } from 'react'
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import SwapHoriz from '@mui/icons-material/SwapHoriz'
import Home from '@mui/icons-material/Home'
import Person from '@mui/icons-material/Person'
import Business from '@mui/icons-material/Business'
import LocalHospital from '@mui/icons-material/LocalHospital'
import TvIcon from '@mui/icons-material/Tv'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { useNavigate } from 'react-router'
import { styled } from '@mui/material/styles'
import { getUserRoles } from 'src/mentor/layout/utils'
import { PORTAL_CONFIG } from 'src/shared/config/portalConfig'

const FooterContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.85) 0%, rgba(99, 102, 241, 0.85) 100%)',
  backdropFilter: 'blur(24px) saturate(200%)',
  WebkitBackdropFilter: 'blur(24px) saturate(200%)',
  borderTop: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
  padding: theme.spacing(0.75, 2.5),
  minHeight: '48px',
  display: 'flex',
  alignItems: 'center',
  marginTop: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.6, 1.5),
    minHeight: '44px',
    background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.75) 0%, rgba(99, 102, 241, 0.75) 100%)',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)',
  },
}))

const FooterContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
}))

const PortalNameContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flex: 1,
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.5),
  },
}))

const PortalSwitchButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  padding: theme.spacing(0.75),
  width: '36px',
  height: '36px',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    transform: 'translateY(-1px)',
  },
  transition: 'all 0.2s ease',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.6),
    width: '32px',
    height: '32px',
  },
}))

const PortalMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    background: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(24px) saturate(180%)',
    WebkitBackdropFilter: 'blur(24px) saturate(180%)',
    border: '1px solid rgba(67, 56, 202, 0.15)',
    borderRadius: '16px',
    boxShadow: '0 -12px 40px rgba(67, 56, 202, 0.25), 0 4px 12px rgba(0, 0, 0, 0.1)',
    minWidth: '280px',
    maxWidth: '320px',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(0.5, 0),
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      bottom: -6,
      right: 20,
      width: 12,
      height: 12,
      background: 'rgba(255, 255, 255, 0.98)',
      borderRight: '1px solid rgba(67, 56, 202, 0.15)',
      borderBottom: '1px solid rgba(67, 56, 202, 0.15)',
      transform: 'translateY(50%) rotate(45deg)',
      zIndex: 0,
    },
  },
  '& .MuiMenuItem-root': {
    fontFamily: "'Inter', sans-serif",
    fontSize: '14px',
    padding: theme.spacing(1.25, 2),
    borderRadius: '8px',
    margin: theme.spacing(0.25, 1),
    transition: 'all 0.2s ease',
    position: 'relative',
    '&:hover': {
      backgroundColor: 'rgba(67, 56, 202, 0.06)',
      transform: 'translateX(2px)',
    },
    '&.Mui-disabled': {
      opacity: 1,
    },
    '&.active': {
      background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.12) 0%, rgba(99, 102, 241, 0.12) 100%)',
      borderLeft: '3px solid rgba(67, 56, 202, 0.8)',
      fontWeight: 600,
      color: '#4338ca',
      '&:hover': {
        backgroundColor: 'rgba(67, 56, 202, 0.15)',
        transform: 'translateX(0)',
      },
      '& .MuiSvgIcon-root': {
        color: '#4338ca',
      },
    },
  },
}))

// Portal configuration is now imported from centralized config (see imports above)

export default function BusinessFooter() {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const userRoles = getUserRoles()

  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  // Get available portals based on user roles
  const availablePortals = PORTAL_CONFIG.filter((portal) =>
    portal.roles.some((role) => userRoles?.includes(role))
  )

  const handlePortalMenuClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handlePortalMenuClose = () => {
    setAnchorEl(null)
  }

  const handlePortalSwitch = (portal) => {
    handlePortalMenuClose()

    setTimeout(() => {
      if (portal.external) {
        window.open(portal.path, '_blank', 'noopener,noreferrer')
      } else if (portal.path === '/') {
        sessionStorage.setItem('explicitlyChoseConsumerPortal', 'true')
        sessionStorage.setItem('chosenSomeFlowInThisSession', 'true')
        sessionStorage.removeItem('portalAutoNavigatedToAtFlow')
        localStorage.removeItem('lastChosenFlow')
        window.location.replace('/')
      } else {
        navigate(portal.path)
      }
    }, 100)
  }

  const isCurrentPortal = (portal) => {
    if (portal.external) return false

    const currentPath = window.location.pathname

    if (portal.path === '/') {
      return (
        currentPath === '/' ||
        (!currentPath.startsWith('/support-home') &&
          !currentPath.startsWith('/mentor-home') &&
          !currentPath.startsWith('/clinic-home') &&
          !currentPath.startsWith('/ca-home') &&
          !currentPath.startsWith('/kiosk') &&
          !currentPath.startsWith('/workplace'))
      )
    }

    return currentPath === portal.path || currentPath.startsWith(portal.path + '/')
  }

  return (
    <FooterContainer>
      <FooterContent>
        <PortalNameContainer>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 500,
              fontSize: isMobile ? '11px' : '13px',
            }}
          >
            AUMHUM Business App
          </Typography>
        </PortalNameContainer>

        {availablePortals.length > 1 && (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{
              marginLeft: 'auto',
              flexShrink: 0,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 500,
                fontSize: isMobile ? '10px' : '11px',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Switch Portal:
            </Typography>
            <PortalSwitchButton
              onClick={handlePortalMenuClick}
              aria-label="Switch portal"
              size="small"
            >
              <SwapHoriz sx={{ fontSize: isMobile ? 18 : 20 }} />
            </PortalSwitchButton>

            <PortalMenu
              anchorEl={anchorEl}
              open={open}
              onClose={handlePortalMenuClose}
              anchorOrigin={{
                vertical: 'top',
                horizontal: isMobile ? 'center' : 'right',
              }}
              transformOrigin={{
                vertical: 'bottom',
                horizontal: isMobile ? 'center' : 'right',
              }}
              PaperProps={{
                sx: {
                  maxHeight: isMobile ? '60vh' : '70vh',
                  overflowY: 'auto',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0, 0, 0, 0.2)',
                    borderRadius: '4px',
                    '&:hover': {
                      background: 'rgba(0, 0, 0, 0.3)',
                    },
                  },
                },
              }}
            >
              {availablePortals.map((portal) => {
                const Icon = portal.icon
                const isActive = isCurrentPortal(portal)
                const shouldDisable = isActive && !portal.external && portal.path !== '/'
                return (
                  <MenuItem
                    key={portal.id}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handlePortalSwitch(portal)
                    }}
                    className={isActive ? 'active' : ''}
                    disabled={shouldDisable}
                    sx={{
                      '&.active': {
                        '& .portal-icon': {
                          color: '#4338ca',
                        },
                        '& .portal-name': {
                          color: '#4338ca',
                          fontWeight: 600,
                        },
                        '& .portal-badge': {
                          background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                          color: '#4338ca',
                          border: '1px solid rgba(67, 56, 202, 0.3)',
                        },
                      },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5} width="100%">
                      <Icon
                        className="portal-icon"
                        sx={{
                          fontSize: 20,
                          color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.6)',
                          transition: 'color 0.2s ease',
                        }}
                      />
                      <Box flex={1}>
                        <Typography
                          className="portal-name"
                          variant="body2"
                          fontWeight={isActive ? 600 : 500}
                          sx={{
                            color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.87)',
                            transition: 'color 0.2s ease',
                          }}
                        >
                          {portal.name}
                        </Typography>
                      </Box>
                      {isActive && (
                        <Box
                          className="portal-badge"
                          sx={{
                            height: '20px',
                            fontSize: '10px',
                            fontWeight: 600,
                            padding: '0 6px',
                            display: 'flex',
                            alignItems: 'center',
                            background: 'linear-gradient(135deg, rgba(67, 56, 202, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                            color: '#4338ca',
                            border: '1px solid rgba(67, 56, 202, 0.3)',
                            borderRadius: '10px',
                          }}
                        >
                          Current
                        </Box>
                      )}
                      {portal.external && (
                        <OpenInNew
                          sx={{
                            fontSize: 16,
                            color: isActive ? '#4338ca' : 'rgba(0, 0, 0, 0.5)',
                          }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                )
              })}
            </PortalMenu>
          </Box>
        )}
      </FooterContent>
    </FooterContainer>
  )
}
